import React, {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import {
	ConvoAIResponseState,
	type ConvoAIStreamError,
	type ConvoAIStreamingState,
	useConvoAiStreamingLazy,
	type UserConvoAIStreamingConfig,
} from '@atlassian/ai-agent-streaming';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest } from '@atlassian/convo-ai-api';

import { useProductDependencies } from '../../../../controllers/use-product-dependencies';

import { messages } from './messages';

const buttonContainerStyles = xcss({
	border: `${token('border.width')} solid ${token('color.border')}`,
	width: 'fit-content',
	marginTop: 'space.075',
	marginBottom: 'space.075',
	borderRadius: 'radius.large',
});

export type SummaryRequestBuilderType = {
	contentAri: string;
	locale: string;
	useCcSmarts?: boolean;
};

export const CONCISE_PAGE_SUMMARY_CC_SMARTS_MIGRATION_EXPERIMENT =
	'cc_smarts_concise_page_summary_migration';
export const CONVO_AI_STREAM_BASE_URL = '/gateway/api/assist/api/ai';
export const CC_SMARTS_STREAM_BASE_URL = '/gateway/api/ai';

type ConcisePageSummaryExperimentReader = (
	experimentName: typeof CONCISE_PAGE_SUMMARY_CC_SMARTS_MIGRATION_EXPERIMENT,
	experimentParam: 'useCcSmarts',
	experimentExpectedValue: true,
	experimentDefaultValue: false,
) => boolean;

export const isConcisePageSummaryCcSmartsRoutingEnabled = (
	readExperiment: ConcisePageSummaryExperimentReader = expValEquals,
): boolean => {
	try {
		return readExperiment(
			CONCISE_PAGE_SUMMARY_CC_SMARTS_MIGRATION_EXPERIMENT,
			'useCcSmarts',
			true,
			false,
		);
	} catch {
		return false;
	}
};

export const buildSummaryRequest = ({
	contentAri,
	locale,
	useCcSmarts = false,
}: SummaryRequestBuilderType): [
	AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
	UserConvoAIStreamingConfig,
] => {
	return [
		{
			ai_feature_input: {
				content_ari: contentAri,
				locale,
				character_max: 95,
			},
		},
		{
			apiPath: 'v2/ai-feature/confluence-concise-summary',
			baseUrl: useCcSmarts ? CC_SMARTS_STREAM_BASE_URL : CONVO_AI_STREAM_BASE_URL,
			delayChunkUpdate: { gap: 10, time: 20 },
			headers: {
				'x-experience-id': 'confluence-concise-summary',
				'x-product': 'confluence',
			},
		},
	];
};

type AIDescriptionButtonProps = {
	analyticsSource: string;
	confluencePageId: string;
	isAILoading?: boolean;
	pageAri: string;
	setAIDescriptionContent: Dispatch<SetStateAction<string | undefined | null>>;
	setAIError: Dispatch<SetStateAction<ConvoAIStreamError | undefined>>;
	setAIResponseState: Dispatch<SetStateAction<ConvoAIResponseState | undefined>>;
	setIsAILoading: Dispatch<SetStateAction<boolean | undefined>>;
};

type AIDescriptionStreamResponse = {
	content: string;
};

export const AIDescriptionButton = ({
	pageAri,
	confluencePageId,
	isAILoading,
	setIsAILoading,
	setAIError,
	setAIDescriptionContent,
	setAIResponseState,
	analyticsSource,
}: AIDescriptionButtonProps): React.JSX.Element => {
	const { aiDescriptionExperienceTracker } = useProductDependencies();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();

	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation();
	const onStateChange = useCallback(
		(state: ConvoAIStreamingState<AIDescriptionStreamResponse>) => {
			switch (state.responseState) {
				case ConvoAIResponseState.Error:
					if (state.error) {
						const { status_code: aiErrorCode, message_template: aiErrorMessage } =
							state.error.message;
						trackAIResultError({
							aiErrorCode,
							aiErrorMessage,
						});
					}
					break;
				case ConvoAIResponseState.FinalResponse:
					if (state.content) {
						trackAIResultView();
					}
					break;
				default:
					break;
			}
		},
		[trackAIResultError, trackAIResultView],
	);
	// The cc-smarts vs convo-ai routing decision is intentionally resolved on click
	// (inside `startAIDescription`) rather than at render. `expValEquals` logs a
	// Statsig exposure the moment it is evaluated, so reading it during render would
	// enrol every user who merely *sees* the button — diluting the experiment with
	// users who never generate a summary. Deferring the read to the click aligns
	// exposure with the population the routing change actually affects.
	// `useCcSmarts` holds the routing arm resolved on the most recent click. `fetchNonce`
	// is a monotonically increasing per-click token: it changes on *every* click even when
	// the resolved arm is identical, so the fetching effect below always re-fires (e.g. when
	// retrying after an error with the same experiment result). Bumping both together in the
	// click handler is what schedules a fetch.
	const [{ useCcSmarts, fetchNonce }, setFetchRequest] = useState<{
		fetchNonce: number;
		useCcSmarts: boolean;
	}>({ useCcSmarts: false, fetchNonce: 0 });

	const [request, config] = useMemo(
		() =>
			buildSummaryRequest({
				contentAri: pageAri,
				locale,
				useCcSmarts,
			}),
		[locale, pageAri, useCcSmarts],
	);

	const [{ content, responseState, error }, fetchAI] = useConvoAiStreamingLazy<
		AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
		AIDescriptionStreamResponse
	>(config, onStateChange);

	// `useConvoAiStreamingLazy` binds `config` at hook-call time, so we can only fetch once
	// `config` reflects the click-time routing decision. This effect runs after a click has
	// bumped `fetchNonce` (and, when the arm changed, `useCcSmarts`/`config`/`request`), then
	// fires the request. `fetchNonce > 0` skips the initial mount; keying the effect on
	// `fetchNonce` guarantees it re-runs on every click even when the routing arm is unchanged.
	useEffect(() => {
		if (fetchNonce > 0) {
			fetchAI(request);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on
		// fetchNonce so repeated clicks with the same arm still trigger a fetch; `request`
		// is derived from `useCcSmarts` which is updated atomically with `fetchNonce`.
	}, [fetchNonce]);

	const startAIDescription = useCallback(() => {
		trackAIInteractionInit();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiDescription',
				source: analyticsSource,
			},
		}).fire();
		aiDescriptionExperienceTracker?.start({ analyticsSource, confluencePageId });

		setIsAILoading(true);
		// Read the experiment here (on click) so the Statsig exposure is click-scoped.
		// This must run *outside* the state updater below: React may invoke an updater
		// twice (e.g. under Strict Mode), which would log a duplicate exposure per click.
		const useCcSmartsRouting = isConcisePageSummaryCcSmartsRoutingEnabled();
		// Bump `fetchNonce` so the fetching effect re-fires even on repeat clicks/retries.
		setFetchRequest((prev) => ({
			useCcSmarts: useCcSmartsRouting,
			fetchNonce: prev.fetchNonce + 1,
		}));
	}, [
		trackAIInteractionInit,
		confluencePageId,
		createAnalyticsEvent,
		aiDescriptionExperienceTracker,
		analyticsSource,
		setIsAILoading,
	]);

	useEffect(() => {
		setAIResponseState(responseState);
		setAIDescriptionContent(content);
		setAIError(error);
	}, [responseState, content, error, setAIResponseState, setAIDescriptionContent, setAIError]);

	return (
		<Box xcss={buttonContainerStyles}>
			<Button
				appearance="subtle"
				onClick={startAIDescription}
				isDisabled={isAILoading}
				iconBefore={AtlassianIntelligenceIcon}
			>
				<FormattedMessage {...messages.suggestDescriptionLabel} />
			</Button>
		</Box>
	);
};
